/**
 * Connect-first routing — the §4 "NO workspace at all" leaf (PDR 011, #4203).
 *
 * Pure decision extracted from the App gate chain so the per-row routing is
 * unit-testable without rendering the whole tree. It answers: when a verified
 * identity has no usable workspace (and the pending-invite gate above did NOT
 * fire), do they land on the connect-first front door or fall through to
 * PlanGate?
 *
 * Under the D18 connect-gate a personal workspace exists only after an
 * email+calendar grant, so "no workspace" === "not connected". A brand-new,
 * never-trialed, non-invitee identity therefore goes to the ConnectPage (D6/D7,
 * no PlanGate at signup — D1). A LAPSED identity (the per-user trial clock is
 * already set — its trial workspace was hard-deleted at day 21) falls through to
 * PlanGate for upgrade / create-or-join-paid, never a silent second free trial
 * (D11/D15). A team member (no owned personal workspace) is likewise excluded
 * from the standalone connect page — they connect inside the team workspace
 * (D16); the pending-invite gate handles the not-yet-accepted case upstream.
 */

export type ZeroWorkspaceDestination = "connect" | "plangate";

export interface ZeroWorkspaceRoutingInput {
  /** `user.trial_started_at` — the per-user trial clock (D11). Null = never trialed. */
  trialStartedAt: number | null | undefined;
  /** True if the user is a member (non-owner) of any workspace (a team membership). */
  hasTeamMembership: boolean;
}

export function resolveZeroWorkspaceDestination(
  input: ZeroWorkspaceRoutingInput,
): ZeroWorkspaceDestination {
  const neverTrialed = (input.trialStartedAt ?? null) === null;
  if (neverTrialed && !input.hasTeamMembership) return "connect";
  return "plangate";
}
